import { useState } from "react";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  Outlet,
  useLoaderData,
  useLocation,
  useSubmit,
} from "react-router";

import NavigationMenu from "@/components/NavigationMenu";
import {
  FeaturesContext,
  PortalContainerContext,
  UserContext,
} from "@/contexts";
import { FeatureFlags, TTrackingCampaign, User } from "@/types";

import PasswordConstraints from "./PasswordConstraints";

export async function loader({ request }: LoaderFunctionArgs) {
  if (
    request.url.includes("/sign_up") ||
    request.url.includes("/phone_number")
  ) {
    return { trackingCampaigns: [], user: undefined };
  }

  try {
    // when not login, we get redirected to the logged in page, we still get a 200 instead of 302, so we need to check the content type
    let user;

    const userResponse = await fetch(`/api/client/users/me/`);

    if (userResponse.status !== 200) {
      console.error("Failed to load the user data");
    }

    if (
      userResponse.headers.get("Content-Type")?.includes("application/json")
    ) {
      const { data: userData } = (await userResponse.json()) as {
        data: User;
      };
      user = userData;
    }

    const featureFlagsResponse = await fetch(`/api/client/features/`);

    if (featureFlagsResponse.status !== 200) {
      throw new Error("Failed to load the user feature flags");
    }

    const { data: featureFlags } = (await featureFlagsResponse.json()) as {
      data: FeatureFlags;
    };

    let sortedTrackingCampaignData = [] as TTrackingCampaign[];

    if (user) {
      const trackingCampaignResponse = await fetch(
        `/api/client/tracking_campaigns`
      );
      if (trackingCampaignResponse.status !== 200) {
        throw new Error("Failed to load the tracking campaign data.");
      }

      const { data: trackingCampaignData } =
        (await trackingCampaignResponse.json()) as {
          data: TTrackingCampaign[];
        };

      sortedTrackingCampaignData = trackingCampaignData.sort(
        (a, b) =>
          new Date(a.period_start).getTime() -
          new Date(b.period_start).getTime()
      );
    }

    return {
      featureFlags,
      trackingCampaigns: sortedTrackingCampaignData,
      user,
    };
  } catch (error) {
    console.error("Failed to load the user data", error);
    return { trackingCampaigns: [], user: undefined };
  }
}

export async function action({ request }: ActionFunctionArgs) {
  if (request.method === "DELETE") {
    // Get CSRF token from meta tag (needed for anything not GET)
    const csrfToken = document?.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    const response = await fetch("/users/sign_out/", {
      method: "delete",
      headers: {
        "X-CSRF-Token": `${csrfToken}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (response.status === 204) {
      window.location.href = "/users/sign_in";
    }
  }
}

function Root() {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const location = useLocation();
  const { featureFlags, trackingCampaigns, user } =
    useLoaderData<typeof loader>();
  const submit = useSubmit();

  const handleLogOutClick = () => {
    submit(null, { method: "DELETE" });
  };

  if (
    location.pathname === "/" ||
    location.pathname === "/users/sign_up" ||
    location.pathname === "/users/invitation/accept" ||
    location.pathname === "/users/password/edit"
  ) {
    return <PasswordConstraints />;
  }
  return (
    <PortalContainerContext value={{ container }}>
      <UserContext value={{ user }}>
        <FeaturesContext value={{ featureFlags }}>
          <div className="fixed top-0 left-0 z-50 flex h-screen">
            {user ? (
              <div className="relative h-screen">
                <NavigationMenu
                  trackingCampaigns={trackingCampaigns}
                  handleLogOutClick={handleLogOutClick}
                />
              </div>
            ) : null}
            <div className="flex-grow overflow-y-auto">
              <Outlet />
            </div>
          </div>
          <div className="relative z-50" ref={setContainer} />
        </FeaturesContext>
      </UserContext>
    </PortalContainerContext>
  );
}

export default Root;
